import { AubadeComponent } from "js/front/bootstrap";
import { PageModule } from 'libs';
import { gsap } from 'gsap';
import { customization } from 'storage';
import {
	AubadeViewer,
	PinsPlugin,
	RotatePlugin,
	MovePlugin,
	DependencyPin,
	ScenePlugin,
	DynamicEnvironmentMapPlugin,
	SpotLightShadowsPlugin,
	FXAAPlugin,
} from 'webgl';
import { APP_URL } from 'js/common/AppUrl';

import { DebugPlugin } from 'webgl/plugins/DebugPlugin';


export class ScenePreview extends AubadeComponent{
	constructor(e, b){
		super(e, b);

		this.sceneLoader 	= document.querySelector("#scene-loader");
		this.loader 		= this.element.querySelector(".fps-loader")
		this.loaderCircle	= this.loader.querySelector(".progress-ring__circle")
		this.pinsContainer 	= this.element.querySelector(".fps-pins-container")
		this.viewContainer 	= this.element.querySelector(".fps-view-container")
		this.viewPos 		= this.element.querySelector(".fps-view-position svg");
		this.banner 		= this.element.querySelector(".fps-banner");
		this.description	= this.element.querySelector(".fps-description");
		this.estimation		= this.element.querySelector(".fps-estimation");
		this.banditmanchot	= this.element.querySelector(".fps-estimation-amount");
		this.amount			= this.element.querySelector(".fps-amount");
		this.continue		= this.element.querySelector(".button-wrapper button");
		this.header =  this.bootstrap.components.get(document.querySelector("header"));

		this.pins = new Map();

		this._viewAngle	= 0;
		this._pins 		= [];
		this._price 	= 0;

		this._callbacks = {
			'editview': []
		};

		this.viewer = new AubadeViewer({
        	plugins:[
				PinsPlugin,
				RotatePlugin,
				MovePlugin,
				FXAAPlugin
        	]
        });

        this.viewContainer.appendChild(this.viewer.domElement);
        this.viewer.domElement.style.opacity = 0;

        this._pinsVisible = false;

        this.firstLoad = true;
	}

	init(){
		this.cSceneLoader = this.components.get(this.sceneLoader);
		this.cBanditManchot = this.components.get(this.banditmanchot);
	}

	attach() {
		this.viewer.attach();
		this.viewer.plugins.get(PinsPlugin).on('pins-changed', this.onPinsChanged);
		this.viewer.rendering.ticker.on('tick', this.tick);

        this.viewer.gltfLoader.on('progress', this.onProgress);

        this.continue.addEventListener('click', this.onContinue);
	}

	detach() {
		this.viewer.detach();
		this.viewer.plugins.get(PinsPlugin).off('pins-changed', this.onPinsChanged);
		this.viewer.rendering.ticker.off('tick', this.tick);
		this.pins.forEach((pin, domPin) => domPin.removeEventListener('click', this.edit));

        this.continue.removeEventListener('click', this.onContinue);
	}

	get camera(){ return this.viewer.rendering.camera; }
	get movePlugin(){ return this.viewer.plugins.get(MovePlugin); }
	get rotatePlugin(){ return this.viewer.plugins.get(RotatePlugin); }

	get pinsVisible(){ return this._pinsVisible; }
	set pinsVisible(visible){
		if(this._pinsVisible != visible){
			this._pinsVisible = visible;
			this.pinsContainer.classList.toggle('visible', visible);
		}
	}

	saveView(){
		this.savedQuaternion 	= this.rotatePlugin.quaternion.clone();
		this.savedPosition 		= this.movePlugin.camera.position.clone();
	}

	restoreView(){
		const t = gsap.timeline();

		const data = {
			lerp:0
		};
		t.fromTo(data, {lerp:0}, {lerp:1, duration:3, ease:'quint.inOut', onUpdate: () => {
			this.camera.position.lerp(this.savedPosition, data.lerp);
			this.viewer.requestRender()
		}}, 0);
		t.fromTo(this.rotatePlugin, {rotationDamping:0}, {rotationDamping:0.1, duration:1, ease:'quint.in'}, 0)

		this.rotatePlugin.quaternion.copy(this.savedQuaternion);
		this.rotatePlugin.euler.setFromQuaternion(this.savedQuaternion);
	}

	onProgress = (p) => {

		const radius = 23;

		const stroke = Math.round(radius * 2 * Math.PI * (1-p));

		this.loaderCircle.style.strokeDashoffset = stroke;

		// this.loader.style.setProperty('--fps-loader-progress', `${(p*100).toFixed(2)}%`);
		if(this.progressTm)
			this.progressTm.kill();

		if(p == 1){
			this.progressTm = gsap.to(this.loader, {opacity:0, duration:0.2, delay:0.5, onComplete:() => {
				this.loader.style.setProperty('--fps-loader-progress', '0%');
				this.loader.style.display = "none";
			}});

			this.firstLoad = false;
		}
		else{
			this.loader.style.display = "block";
			this.progressTm = gsap.to(this.loader, {opacity:1, duration:0.2});
		}
	}

	onPinsChanged = () => {
		this.updatePins(this.viewer.plugins.get(PinsPlugin).pins);
	}

	onContinue = async (e) => {
		e.preventDefault();

		await customization.remoteSave();

		this.router.set(`${APP_URL}/panier/${customization.uuid}`);

		dataLayer.push({'event': 'Validation du Simulateur SdB'});
	}

	updatePins(pins){
		this.pins.forEach((pin, domPin) => domPin.removeEventListener('click', this.edit))
		this.pins.clear();

		const pinsArr = [...pins.values()];
		this.pinsContainer.innerHTML = pinsArr.reduce((acc, pin) => acc += this.pinTemplate(pin), '');
		this.pinsContainer.querySelectorAll(".fps-pin-plus").forEach(domPin => {
			const pin = pinsArr.find(pin => pin.code == domPin.dataset.entityType);

			if(pin){
				this.pins.set(domPin, pin);
				domPin.addEventListener('click', this.edit);
			}
		});
	}

	pinTemplate(pin){
		if(pin instanceof DependencyPin)
			return `<button class="fps-pin-plus" data-entity-type="${pin.code}" data-entity-dependency="${pin.dependencyCode}"><i class="icon plus"></i></button>`;
		if(pin.isGhost)
			return `<button class="fps-pin-plus fps-pin-ghost" data-entity-type="${pin.code}"><i class="icon plus"></i><span>Ajouter ce produit</span></button>`;
		return `<button class="fps-pin-plus" data-entity-type="${pin.code}"><i class="icon plus"></i></button>`;
	}

	tick = () => {
		this.pins.forEach((pin, domPin) => {
			domPin.style.transform = `translate3d(${pin.screen.x * this.viewport.width * 0.5}px, ${pin.screen.y * this.viewport.height * 0.5}px, 0px)`;
			domPin.style.display = pin.visible && !pin.collisionHidden ? 'block':'none';
		});

		this.updateViewAngle();

		this.pinsVisible = !this.movePlugin.moving && !this.rotatePlugin.rotating;
	}

	updateViewAngle(){
		this.viewAngle = -this.viewer.plugins.get(RotatePlugin).euler.y * 180/Math.PI;
	}


	get isFromChooseProduct(){
		const previous = this.bootstrap.get(PageModule).previous;
		return previous && previous.dataset.component == "ChooseProducts";
	}

	open(){
        this.viewer.resize();
        this.viewer.start();

        const hasPrettyLoader = this.isFromChooseProduct;

		const scenePlugin = this.viewer.plugins.get(ScenePlugin);

		const anim = (() => {
			const t = gsap.timeline({delay:1});
			if(hasPrettyLoader)
				t.add(this.cSceneLoader.hide(), 0);
			t.fromTo(this.viewer.domElement, {opacity: 0}, {opacity: 1, duration: 0.5}, 0);
			t.add(this.showUI());
			return t;
		})()

		const t = gsap.timeline();

		if(hasPrettyLoader)
			this.cSceneLoader.show();
		else this.cSceneLoader.remove();

		if(scenePlugin.scene){
			t.add(anim);
		}
		else{
			anim.pause();
			scenePlugin.once('scene-changed', () => {
				//Delay a bit as first render takes some time
				setTimeout(() => {
					anim.play(0);
				}, 1000)
			})
		}
		return t;
	}
	close(){
		const t = gsap.timeline();
        this.viewer.stop();
		t.add(this.hideUI());
		t.to(this.viewer.domElement, {opacity: 0, duration: 0.5}, 0);
		return t;
	}

	showUI(){
		const t = gsap.timeline();
		t.add(() => this.pinsContainer.classList.remove('hidden'), 0);
		t.fromTo(this.viewPos, {scale: 0.5, }, {scale: 1, duration: 1, ease:'expo.out'}, 0.4);
		t.fromTo(this.viewPos, {opacity: 0}, {opacity:1, duration: 0.5}, 0.4);
		t.fromTo(this.banner, {opacity:0}, {opacity:1, duration: 0.5, ease:'none'}, 0.3);
		t.fromTo(this.banner, {y: this.banner.offsetHeight}, {y: 0, duration: 1, ease:'expo.out'}, 0.3);
		t.add(() => {
			this.cBanditManchot.hold = false;
			this.cBanditManchot.roll(this.cBanditManchot.value);
		})
		return t;
	}

	hideUI(){
		this.cBanditManchot.hold = true;
		const t = gsap.timeline();
		t.add(() => this.pinsContainer.classList.add('hidden'), 0);
		t.to(this.viewPos, {opacity: 0, duration: 0.5}, 0);
		t.to(this.banner, {y: this.banner.offsetHeight, duration: 0.5, ease:'expo.in'}, 0);
		t.to(this.banner, {opacity:0, duration: 0.5, ease:'none'}, 0);
		return t;
	}


	set viewAngle(angle){
		this._viewAngle = angle;
		gsap.set(this.viewPos, {rotate:this._viewAngle});
	}

	get viewAngle() {
		return this._viewAngle;
	}

	set price(value) {
		this._price = value;
		this.amount.innerText = value
	}
	get price() {
		return this._price;
	}

	edit = (e) => {

		this.saveView();
		const domPin = e.currentTarget

		const pin = this.pins.get(domPin);
		this.viewer.plugins.get(RotatePlugin).focus(pin);
		this.viewer.plugins.get(MovePlugin).moveToPin(pin);

		this.header.hideHeader();
		
		const {
			product:prd,
			quantity:qty,
			isUser
		} = customization.products.get(domPin.dataset.entityType);

		let delay = false;
		if(!isUser){
			customization.set(prd.code, qty, true);
			delay = true;
		}

		setTimeout(() => {
			if('entityDependency' in domPin.dataset){
				this.emit('editview', {
					code:domPin.dataset.entityType,
					dependency:domPin.dataset.entityDependency
				})	
			}
			else{
				this.emit('editview', {
					code:domPin.dataset.entityType
				})	
			}
		}, delay ? 1000:0)
	}
}